import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken, clearToken } from './App';
import i18n from './i18n';
import Layout from './Layout';
import { Ico } from './icons';


function Dashboard() {
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let u = null;
        try {
            u = JSON.parse(localStorage.getItem('nexus_user') || 'null');
            setUser(u);
        } catch {
            setUser(null);
        }

        if (u && u.role === 'superadmin') {
            setLoaded(true);
            return;
        }

        const token = getToken();
        fetch('/api/clock/history', {
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .then((res) => {
                if (res.status === 401) {
                    clearToken();
                    localStorage.removeItem('nexus_user');
                    navigate('/login', { replace: true });
                    throw new Error(t('common.session_expired'));
                }
                if (!res.ok) throw new Error(t('common.http_error', { status: res.status }));
                return res.json();
            })
            .then((data) => {
                setHistory(data.history || []);
                setLoaded(true);
            })
            .catch((err) => {
                setLoaded(true);
                if (err.message !== t('common.session_expired')) {
                    setError(err.message);
                }
            });
    }, [navigate, t]);

    const localeMap = { es: 'es-ES', ca: 'ca-ES', en: 'en-US' };
    const locale = localeMap[i18n.language?.split('-')[0]] || 'es-ES';

    const formatTime = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleString(locale, {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
            });
        } catch {
            return iso;
        }
    };

    const fmtShort = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleString(locale, {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return iso;
        }
    };

    const todayKey = new Date().toDateString();
    const stats = (() => {
        const total = history.length;
        let today = 0;
        const people = new Set();
        let lastTs = 0;
        for (const h of history) {
            if (h.clock_in) {
                const d = new Date(h.clock_in);
                if (!Number.isNaN(d.getTime())) {
                    if (d.toDateString() === todayKey) today += 1;
                    if (d.getTime() > lastTs) lastTs = d.getTime();
                }
            }
            if (h.employee_name) people.add(h.employee_name);
        }
        return {
            total,
            today,
            employees: people.size,
            last: lastTs ? new Date(lastTs) : null,
        };
    })();

    const initials = (name) => {
        const p = (name || '?').trim().split(/\s+/).filter(Boolean);
        if (p.length === 0) return '?';
        if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
        return (p[0][0] + p[p.length - 1][0]).toUpperCase();
    };

    const sourceBadge = (src) => {
        const s = (src || '').toLowerCase();
        if (s.includes('nfc')) {
            return <span className="badge badge-accent"><span className="dot" /><Ico name="fingerprint" size={11} />{src}</span>;
        }
        return <span className="badge badge-info"><span className="dot" />{src || '—'}</span>;
    };

    const fmtDuration = (raw) => {
        if (!raw) return null;
        let m = null;
        const dm = /^(\d+)d\s+(\d{2}):(\d{2}):(\d{2})$/.exec(raw);
        const hm = /^(\d{2}):(\d{2}):(\d{2})$/.exec(raw);
        if (dm) m = Number(dm[1]) * 1440 + Number(dm[2]) * 60 + Number(dm[3]) + Math.round(Number(dm[4]) / 60);
        else if (hm) m = Number(hm[1]) * 60 + Number(hm[2]) + Math.round(Number(hm[3]) / 60);
        if (m === null) return raw;
        const h = Math.floor(m / 60);
        const min = m % 60;
        return `${h}h ${String(min).padStart(2, '0')}m`;
    };

    const greeting = () => {
        const h = new Date().getHours();
        if (h < 6) return t('dashboard.g_night', 'Buenas noches');
        if (h < 13) return t('dashboard.g_morning', 'Buenos días');
        if (h < 20) return t('dashboard.g_afternoon', 'Buenas tardes');
        return t('dashboard.g_evening', 'Buenas noches');
    };

    return (
        <Layout
            title={t('common.dashboard')}
            subtitle={`${greeting()}, ${user?.full_name?.split(' ')[0] || user?.email || t('common.user')}`}
            actions={
                <span className="meta-chip" title={t('common.tenant')}>
                    <Ico name="building" size={13} />
                    <code>{user?.tenant_id?.slice(0, 8) || '—'}</code>
                </span>
            }
        >
            {user?.role === 'superadmin' ? (
                <section className="card">
                    <div className="empty-state">
                        <div className="empty-ico"><Ico name="building" size={27} /></div>
                        <div className="empty-title">{t('dashboard.superadmin_panel_title')}</div>
                        <div className="empty-sub">{t('dashboard.superadmin_panel_empty')}</div>
                        <div style={{ marginTop: 12 }}>
                            <Link to="/superadmin/tenants" className="btn btn-primary">
                                <Ico name="building" size={16} />
                                {t('superadmin.nav_tenants_plain', 'Administrar Tenants')}
                            </Link>
                        </div>
                    </div>
                </section>
            ) : (
                <>
                    <div className="stat-grid">
                        <div className="stat-card">
                            <div className="stat-ico tint-accent"><Ico name="activity" size={19} /></div>
                            <div>
                                <div className="stat-val num">{stats.total}</div>
                                <div className="stat-lab">{t('dashboard.stat_total', 'Fichajes totales')}</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-ico tint-success"><Ico name="clock" size={19} /></div>
                            <div>
                                <div className="stat-val num">{stats.today}</div>
                                <div className="stat-lab">{t('dashboard.stat_today', 'Fichajes hoy')}</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-ico tint-info"><Ico name="users" size={19} /></div>
                            <div>
                                <div className="stat-val num">{stats.employees}</div>
                                <div className="stat-lab">{t('dashboard.stat_people', 'Empleados con actividad')}</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-ico tint-warn"><Ico name="calendar" size={19} /></div>
                            <div>
                                <div className="stat-val" style={{ fontSize: '1.05rem', lineHeight: 1.5 }}>
                                    {stats.last ? fmtShort(stats.last.toISOString()) : '—'}
                                </div>
                                <div className="stat-lab">{t('dashboard.stat_last', 'Última actividad')}</div>
                            </div>
                        </div>
                    </div>

                    <section className="card">
                        <div className="card-head">
                            <div>
                                <div className="card-title">{t('dashboard.activity_title', 'Actividad reciente')}</div>
                                <div className="card-sub">{t('dashboard.last_clockings')}</div>
                            </div>
                            <div className="card-tools">
                                <Link to="/dashboard/reports" className="btn btn-ghost btn-sm">
                                    <Ico name="chart" size={14} />
                                    {t('common.reports')}
                                </Link>
                            </div>
                        </div>

                        {error && (
                            <div className="alert alert-error" role="alert">
                                <Ico name="alert" size={17} />
                                <span>{error}</span>
                            </div>
                        )}

                        {!loaded ? (
                            <div className="skel-rows">
                                {[0, 1, 2, 3, 4].map((i) => <div key={i} className="skel skel-row" />)}
                            </div>
                        ) : history.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-ico"><Ico name="inbox" size={27} /></div>
                                <div className="empty-title">{t('dashboard.empty_title', 'Aún no hay fichajes')}</div>
                                <div className="empty-sub">{t('dashboard.no_clockings')}</div>
                            </div>
                        ) : (
                            <div className="table-wrap">
                                <table className="nx-table">
                                    <thead>
                                        <tr>
                                            <th>{t('dashboard.table_employee')}</th>
                                            <th>{t('dashboard.table_clock_in')}</th>
                                            <th>{t('dashboard.table_clock_out')}</th>
                                            <th>{t('dashboard.table_duration')}</th>
                                            <th>{t('dashboard.table_source')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.slice(0, 20).map((h) => (
                                            <tr key={h.id}>
                                                <td>
                                                    <div className="emp-cell">
                                                        <div className="avatar sm">{initials(h.employee_name)}</div>
                                                        <div className="emp-meta">
                                                            <div className="emp-name">{h.employee_name}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="num">{formatTime(h.clock_in)}</td>
                                                <td className="num">
                                                    {h.clock_out
                                                        ? formatTime(h.clock_out)
                                                        : <span className="badge badge-success"><span className="dot pulse" />{t('dashboard.active_session', 'En curso')}</span>}
                                                </td>
                                                <td>
                                                    {h.duration
                                                        ? <span className="chip-code">{fmtDuration(h.duration)}</span>
                                                        : <span className="muted">—</span>}
                                                </td>
                                                <td>{sourceBadge(h.source)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </>
            )}
        </Layout>
    );
}

export default Dashboard;
